// Agents catalog page — shows the managed agent pool

const AGENT_CATALOG = [
  { id: 'val-orch',    name: 'Valuation Orchestrator', cat: 'Diligence',  tone: 'blue',   model: 'sonnet-4-5', desc: 'Coordinates valuation specialists; produces synthesized football-field range and memo bullets.', runs: 142, avgMs: 8400 },
  { id: 'comps',       name: 'Public Comps Analyst',   cat: 'Diligence',  tone: 'teal',   model: 'haiku-4-5',  desc: 'Builds peer set, applies size and quality discounts, hands EV/EBITDA bands to orchestrator.', runs: 137, avgMs: 1670 },
  { id: 'precedent',   name: 'Precedent Transactions', cat: 'Diligence',  tone: 'violet', model: 'haiku-4-5',  desc: 'Finds and filters M&A comps from last 36 months; adjusts for control premium and synergies.', runs: 128, avgMs: 1840 },
  { id: 'lbo',         name: 'LBO Modeler',            cat: 'Diligence',  tone: 'amber',  model: 'sonnet-4-5', desc: 'Builds operating model; solves for entry multiple at 22% target IRR with sensitivity grid.', runs: 91,  avgMs: 2890 },
  { id: 'qoe',         name: 'QofE Risk Scanner',      cat: 'Diligence',  tone: 'rose',   model: 'haiku-4-5',  desc: 'Reads data room; flags concentration, deferred revenue, GAAP, and add-back risks.', runs: 211, avgMs: 3120 },
  { id: 'sourcing',    name: 'Buyer Sourcing Analyst', cat: 'Origination',tone: 'teal',   model: 'haiku-4-5',  desc: 'Builds shortlist of strategic + financial buyers from sector, geography, and historical activity.', runs: 78,  avgMs: 2410 },
  { id: 'thesis',      name: 'Thesis Drafter',         cat: 'Origination',tone: 'amber',  model: 'sonnet-4-5', desc: 'Drafts opportunity thesis, key risks, value creation playbook in committee-ready prose.', runs: 64,  avgMs: 4220 },
  { id: 'memo',        name: 'IC Memo Writer',         cat: 'Content',    tone: 'violet', model: 'sonnet-4-5', desc: 'Assembles final IC memo from workspace artifacts, citing sources inline.', runs: 41,  avgMs: 7100 },
  { id: 'outreach',    name: 'Outreach Drafter',       cat: 'Content',    tone: 'blue',   model: 'haiku-4-5',  desc: 'Personalized intro emails to founders; pulls from CRM relationship graph.', runs: 162, avgMs: 920 },
  { id: 'dq',          name: 'Data Quality Auditor',   cat: 'Operations', tone: 'slate',  model: 'haiku-4-5',  desc: 'Scans CRM for duplicates, mismatches, stale records; proposes cleanup tasks.', runs: 32,  avgMs: 5800 },
];

const AgentsView = ({ catalog, goToSettings, openAgentPage }) => {
  const cats = ['All', 'Origination', 'Diligence', 'Content', 'Operations'];
  const [cat, setCat] = React.useState('All');

  // Self-service agents own a standalone page (Run button + results);
  // everything else executes inside a deal/company workspace (see the deal
  // detail "New workspace" flow) — point the user there instead of silently
  // doing nothing.
  const SELF_SERVICE = new Set(['lost-deals-finder']);
  const explainRun = (agent) => {
    if (SELF_SERVICE.has(agent.id) && openAgentPage) { openAgentPage(agent.id); return; }
    pushToast({
      type: 'info',
      title: 'Run agents from a workspace',
      body: `Open an engagement or company and tap "New workspace" to put ${agent.name || 'an agent'} on it.`,
    });
  };

  // Adapt live API catalog to the view's display shape; fall back to mock
  const TONES = ['blue', 'teal', 'violet', 'amber', 'rose', 'slate'];
  const titleCase = (s) => (s || '').replace(/(^|[\s-])\w/g, (m) => m.toUpperCase()).replace(/-/g, ' ');
  const adapted = (catalog && catalog.length)
    ? catalog.map((a, i) => ({
        id: a.key,
        name: a.label,
        cat: titleCase(a.category),
        tone: TONES[i % TONES.length],
        model: null,
        desc: a.description,
        runs: null,
      }))
    : AGENT_CATALOG;

  const list = cat === 'All' ? adapted : adapted.filter(a => a.cat === cat);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Agents']}
        title="Agent catalog"
        actions={(
          <>
            <Btn size="sm" variant="outline" icon={<Icon.Folder size={12} />}
              onClick={() => goToSettings ? goToSettings('workflows') : null}>Workflows</Btn>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />}
              onClick={() => goToSettings ? goToSettings('agents') : null}>New agent</Btn>
          </>
        )}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36 }}>
        {cats.map((c) => {
          const n = c === 'All' ? adapted.length : adapted.filter(a => a.cat === c).length;
          const active = cat === c;
          return (
            <button key={c} onClick={() => setCat(c)} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>{c}<span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">{n}</span></button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {list.map((a) => {
            const tone = HUE[a.tone] || HUE.slate;
            return (
              <div key={a.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14,
                display: 'flex', flexDirection: 'column', gap: 10, transition: 'border-color .12s, box-shadow .12s',
                cursor: 'pointer',
              }}
                onClick={() => { if (SELF_SERVICE.has(a.id) && openAgentPage) openAgentPage(a.id); }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon.Bot size={16} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <Pill hue={a.tone} sz="sm">{a.cat}</Pill>
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{a.model}</span>
                    </div>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>{a.desc}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-3)' }} className="num">
                    <span>{a.runs} runs</span>
                  </div>
                  <Btn size="xs" variant="outline" icon={<Icon.Play size={10} />}
                    onClick={(e) => { e.stopPropagation(); explainRun(a); }}>Run</Btn>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

window.AgentsView = AgentsView;
